import i18next from 'i18next';
import React from 'react';

import { Button } from '@commons/utils/styledLibraryComponents';
import {
  canChangeTransferStatusToDraft,
  canChangeTransferStatusToInvoiced,
  canDeleteTransferV2,
  canCancelSendTransfer,
  canRequestTransfer,
  canSendTransfer,
  canCreateByBatch,
} from '@selectors/actions/transferActions';
import { TRANSFER_STATUS } from '@stocks/StocksInventories/Transfers/utils/constants';

export const getGoToTransferActions = (goToCreateTransfer, authorizedActions) => {
  const actions = [];

  if (canRequestTransfer(authorizedActions)) {
    actions.push({
      id: 'ask_for_transfer',
      label: i18next.t('STOCKS.TRANSFERS.ACTION_ASK_FOR_TRANSFER'),
      icon: '/images/inpulse/arrow-down-ipblack.svg',
      handleClick: () => goToCreateTransfer(false),
    });
  }

  if (canSendTransfer(authorizedActions)) {
    actions.push({
      id: 'send_transfer',
      label: i18next.t('STOCKS.TRANSFERS.ACTION_SEND_TRANSFER'),
      icon: '/images/inpulse/arrow-up-ipblack.svg',
      handleClick: () => goToCreateTransfer(true),
    });
  }

  return actions;
};

// Row / bulk actions
export const getActions = ({
  goToCreateTransfer,
  authorizedActions,
  selectedTransferLists,
  userSalesPointStores,
  setToInvoiced,
  cancelInvoiced,
  setToDraft,
  cancelReception,
  cancelSend,
  openDeleteConfirmationModal,
  exportList,
  triggerExportContent,
  downloadTransferBatchCreationFileTemplate,
  openTransferBatchCreationFileImportModal,
}) => {
  const actions = [];

  const singleSelected = selectedTransferLists.length === 1;

  actions.push({
    id: 'action-create',
    fixed: canRequestTransfer(authorizedActions) || canSendTransfer(authorizedActions),
    actionLabel: i18next.t('GENERAL.CREATE'),
    isHidden: () => !canRequestTransfer(authorizedActions) && !canSendTransfer(authorizedActions),
    render: () => (
      <Button
        actions={getGoToTransferActions(goToCreateTransfer, authorizedActions)}
        color={'inpulse-default'}
        icon={'/images/inpulse/add-white-small.svg'}
        label={i18next.t('GENERAL.CREATE')}
        hideActionsIcon
      />
    ),
  });

  if (selectedTransferLists.length >= 1) {
    actions.push(
      {
        id: 'set_to_invoiced',
        actionLabel: () =>
          i18next.t('STOCKS.TRANSFERS.SET_TO_INVOICED', { count: selectedTransferLists.length }),
        actionIcon: () => '/images/inpulse/edit-pen-black-small.svg',
        handleAction: () => setToInvoiced(selectedTransferLists),
        isHidden: () =>
          !canSetToInvoiced(selectedTransferLists, userSalesPointStores, authorizedActions),
      },
      {
        id: 'cancel_invoiced',
        actionLabel: () =>
          i18next.t('STOCKS.TRANSFERS.CANCEL_INVOICED', { count: selectedTransferLists.length }),
        actionIcon: () => '/images/inpulse/edit-pen-black-small.svg',
        handleAction: () => cancelInvoiced(selectedTransferLists),
        isHidden: () =>
          !canCancelInvoiced(selectedTransferLists, userSalesPointStores, authorizedActions),
      },
      {
        id: 'set_to_draft',
        actionLabel: () =>
          i18next.t('STOCKS.TRANSFERS.SET_TO_DRAFT', { count: selectedTransferLists.length }),
        actionIcon: () => '/images/inpulse/edit-pen-black-small.svg',
        handleAction: () => setToDraft(selectedTransferLists),
        isHidden: () =>
          !canSetToDraft(selectedTransferLists, userSalesPointStores, authorizedActions),
      },
      {
        id: 'cancel_reception',
        actionLabel: () =>
          i18next.t('STOCKS.TRANSFERS.CANCEL_RECEPTION', { count: selectedTransferLists.length }),
        actionIcon: () => '/images/inpulse/close-black-small.svg',
        handleAction: () => cancelReception(selectedTransferLists),
        isHidden: () =>
          !canCancelReception(selectedTransferLists, userSalesPointStores, authorizedActions),
      },
      {
        id: 'cancel_send',
        actionLabel: () =>
          i18next.t('STOCKS.TRANSFERS.CANCEL_SEND', { count: selectedTransferLists.length }),
        actionIcon: () => '/images/inpulse/edit-pen-black-small.svg',
        handleAction: () => cancelSend(selectedTransferLists),
        isHidden: () =>
          !canCancelSend(selectedTransferLists, userSalesPointStores, authorizedActions),
      },
    );
  }

  actions.push(
    {
      id: 'export_list',
      actionLabel: i18next.t(
        selectedTransferLists.length > 0
          ? 'STOCKS.STOCKS.FORM_EXPORT_SELECTION_BUTTON'
          : 'GENERAL.EXPORT_LIST',
      ),
      actionIcon: () => '/images/inpulse/file-download-black-small.svg',
      handleAction: () => exportList(selectedTransferLists),
    },
    {
      id: 'export_content',
      actionLabel: i18next.t(
        singleSelected ? 'GENERAL.EXPORT_CONTENT_SINGULAR' : 'GENERAL.EXPORT_CONTENT_PLURAL',
      ),
      actionIcon: () => '/images/inpulse/file-download-black-small.svg',
      handleAction: () => triggerExportContent(selectedTransferLists),
    },
  );

  if (canCreateByBatch(authorizedActions)) {
    actions.push(
      {
        id: 'download_batch_creation_file_template',
        actionLabel: i18next.t('STOCKS.TRANSFERS.ACTION_DOWNLOAD_BATCH_CREATION_FILE_TEMPLATE'),
        actionIcon: () => '/images/inpulse/file-download-black-small.svg',
        handleAction: () => downloadTransferBatchCreationFileTemplate(),
      },
      {
        id: 'import_batch_creation_file',
        actionLabel: i18next.t('STOCKS.TRANSFERS.ACTION_IMPORT_BATCH_CREATION_FILE'),
        actionIcon: () => '/images/inpulse/file-upload-black-small.svg',
        handleAction: () => openTransferBatchCreationFileImportModal(),
      },
    );
  }

  if (
    singleSelected &&
    allowTransferDeletion(selectedTransferLists[0], userSalesPointStores, authorizedActions)
  ) {
    actions.push({
      id: 'delete_transfer',
      actionLabel: i18next.t('STOCKS.TRANSFERS.DELETE_TRANSFER'),
      actionIcon: () => '/images/inpulse/delete-ip-black-small.svg',
      handleAction: () => openDeleteConfirmationModal(selectedTransferLists[0].id),
    });
  }

  return actions;
};

// Row actions
export const getRowActions = ({
  userSalesPointStores,
  authorizedActions,
  setToInvoiced,
  cancelInvoiced,
  setToDraft,
  cancelReception,
  cancelSend,
  openDeleteConfirmationModal,
  exportList,
  triggerExportContent,
}) => {
  const actions = [
    {
      id: 'set_to_invoiced',
      actionLabel: () => i18next.t('STOCKS.TRANSFERS.SET_TO_INVOICED', { count: 1 }),
      actionIcon: () => '/images/inpulse/edit-pen-black-small.svg',
      handleAction: (transferList) => setToInvoiced([transferList]),
      isHidden: (transferList) =>
        !canSetToInvoiced([transferList], userSalesPointStores, authorizedActions),
    },
    {
      id: 'cancel_invoiced',
      actionLabel: () => i18next.t('STOCKS.TRANSFERS.CANCEL_INVOICED', { count: 1 }),
      actionIcon: () => '/images/inpulse/edit-pen-black-small.svg',
      handleAction: (transferList) => cancelInvoiced([transferList]),
      isHidden: (transferList) =>
        !canCancelInvoiced([transferList], userSalesPointStores, authorizedActions),
    },
    {
      id: 'set_to_draft',
      actionLabel: () => i18next.t('STOCKS.TRANSFERS.SET_TO_DRAFT', { count: 1 }),
      actionIcon: () => '/images/inpulse/edit-pen-black-small.svg',
      handleAction: (transferList) => setToDraft([transferList]),
      isHidden: (transferList) =>
        !canSetToDraft([transferList], userSalesPointStores, authorizedActions),
    },
    {
      id: 'cancel_reception',
      actionLabel: () => i18next.t('STOCKS.TRANSFERS.CANCEL_RECEPTION', { count: 1 }),
      actionIcon: () => '/images/inpulse/close-black-small.svg',
      handleAction: (transferList) => cancelReception([transferList]),
      isHidden: (transferList) =>
        !canCancelReception([transferList], userSalesPointStores, authorizedActions),
    },
    {
      id: 'cancel_send',
      actionLabel: () => i18next.t('STOCKS.TRANSFERS.CANCEL_SEND', { count: 1 }),
      actionIcon: () => '/images/inpulse/edit-pen-black-small.svg',
      handleAction: (transferList) => cancelSend([transferList]),
      isHidden: (transferList) =>
        !canCancelSend([transferList], userSalesPointStores, authorizedActions),
    },
    {
      id: 'delete_transfer',
      actionLabel: () => i18next.t('STOCKS.TRANSFERS.DELETE_TRANSFER'),
      actionIcon: () => '/images/inpulse/delete-ip-black-small.svg',
      handleAction: (transferList) => openDeleteConfirmationModal(transferList.id),
      isHidden: (transferList) =>
        !allowTransferDeletion(transferList, userSalesPointStores, authorizedActions),
    },
    {
      id: 'export_list',
      actionLabel: () => i18next.t('STOCKS.TRANSFERS.EXPORT_TRANSFER'),
      actionIcon: () => '/images/inpulse/file-download-black-small.svg',
      handleAction: (transferList) => exportList([transferList]),
    },
    {
      id: 'export_content',
      actionLabel: () => i18next.t('GENERAL.EXPORT_CONTENT_SINGULAR'),
      actionIcon: () => '/images/inpulse/file-download-black-small.svg',
      handleAction: (transferList) => triggerExportContent([transferList]),
    },
  ];

  return actions;
};

const canSetToInvoiced = (transferLists, stores, authorizedActions) => {
  if (!canChangeTransferStatusToInvoiced(authorizedActions)) {
    return false;
  }

  const storeIds = new Set(stores.map(({ id }) => id));

  return transferLists.every(({ senderStore, status }) => {
    const isAssociatedToSender = storeIds.has(senderStore.id);

    const isValidStatus = [TRANSFER_STATUS.NON_COMPLIANT, TRANSFER_STATUS.COMPLIANT].includes(
      Number(status),
    );

    return isAssociatedToSender && isValidStatus;
  });
};

export const canCancelInvoicedTransferList = (
  { senderStoreId, status },
  stores,
  authorizedActions,
) => {
  if (!canChangeTransferStatusToInvoiced(authorizedActions)) {
    return false;
  }

  const isAssociatedToSender = stores.some(({ id }) => id === senderStoreId);

  const isValidStatus = Number(status) === TRANSFER_STATUS.INVOICED;

  return isAssociatedToSender && isValidStatus;
};

const canCancelInvoiced = (transferLists, stores, authorizedActions) =>
  transferLists.every(({ senderStore, status }) =>
    canCancelInvoicedTransferList(
      { senderStoreId: senderStore.id, status },
      stores,
      authorizedActions,
    ),
  );

const canCancelSend = (transferLists, stores, authorizedActions) => {
  if (!canCancelSendTransfer(authorizedActions)) {
    return false;
  }

  const storeIds = new Set(stores.map(({ id }) => id));

  return transferLists.every(({ senderStore, status, isSender }) => {
    const isAssociatedToSender = storeIds.has(senderStore.id);

    const isValidStatus = Number(status) === TRANSFER_STATUS.SENT;

    return isAssociatedToSender && isValidStatus && !isSender;
  });
};

const canCancelReception = (transferLists, stores, authorizedActions) => {
  if (!canCancelSendTransfer(authorizedActions)) {
    return false;
  }

  const storeIds = new Set(stores.map(({ id }) => id));

  return transferLists.every(({ recipientStore, status }) => {
    const isAssociatedToRecipient = storeIds.has(recipientStore.id);

    const isValidStatus = [TRANSFER_STATUS.COMPLIANT, TRANSFER_STATUS.NON_COMPLIANT].includes(
      Number(status),
    );

    return isAssociatedToRecipient && isValidStatus;
  });
};

const canSetToDraft = (transferLists, stores, authorizedActions) => {
  if (!canChangeTransferStatusToDraft(authorizedActions)) {
    return false;
  }

  const storeIds = new Set(stores.map(({ id }) => id));

  return transferLists.every(({ senderStore, recipientStore, isSender, status: statusString }) => {
    const isAssociatedToSender = storeIds.has(senderStore.id);
    const isAssociatedToRecipient = storeIds.has(recipientStore.id);

    const status = Number(statusString);

    const isSenderEligible = isSender && isAssociatedToSender && status === TRANSFER_STATUS.SENT;
    const isRecipientEligible =
      !isSender && isAssociatedToRecipient && status === TRANSFER_STATUS.REQUESTED;

    return isSenderEligible || isRecipientEligible;
  });
};

const allowTransferDeletion = (transferList, stores, authorizedActions) => {
  if (!canDeleteTransferV2(authorizedActions)) {
    return false;
  }

  const storeIds = new Set(stores.map(({ id }) => id));

  if (parseInt(transferList.status) !== TRANSFER_STATUS.DRAFT) {
    return false;
  }

  if (transferList.isSender) {
    return storeIds.has(transferList.senderStore.id);
  }

  return storeIds.has(transferList.recipientStore.id);
};
